import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/i18n";
import { api, resolveImg } from "@/lib/api";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { BarsMark } from "@/components/site/Marks";

const Gallery = () => {
  const { t, lang } = useLang();
  const [paintings, setPaintings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null); // index in filtered list

  useEffect(() => {
    api.get("/paintings").then((r) => setPaintings(r.data)).catch(() => {});
  }, []);

  const filtered = filter === "all" ? paintings : paintings.filter((p) => p.category === filter);

  const cats = ["all", "vystava1", "vystava2", "nic"];
  const catLabel = (c) => (c === "all" ? t("gallery.all") : t(`gallery.cats.${c}`));

  const close = useCallback(() => setActive(null), []);
  const prev = useCallback(() => setActive((i) => (i > 0 ? i - 1 : filtered.length - 1)), [filtered.length]);
  const next = useCallback(() => setActive((i) => (i < filtered.length - 1 ? i + 1 : 0)), [filtered.length]);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active, close, prev, next]);

  const current = active !== null ? filtered[active] : null;

  const handleInterest = (number, title) => {
    close();
    window.dispatchEvent(new CustomEvent("prefill-contact", { detail: { number, title } }));
    setTimeout(() => {
      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    }, 120);
  };

  return (
    <section id="gallery" className="relative py-24 md:py-36 px-6 md:px-10">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div className="max-w-xl">
            <BarsMark className="opacity-50 mb-4" color="#d97925" count={3} w={36} />
            <p className="overline text-[#d97925] mb-4">{t("gallery.overline")}</p>
            <h2 className="font-serif-display font-light text-5xl md:text-6xl lg:text-7xl tracking-tight">
              {t("gallery.title")}
            </h2>
            <p className="mt-6 text-[#a3a09a] leading-relaxed font-light">{t("gallery.intro")}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {cats.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c}`}
                onClick={() => setFilter(c)}
                className={`overline px-4 py-2 rounded-full border transition-colors ${
                  filter === c
                    ? "border-[#b02418] text-[#d97925]"
                    : "border-white/12 text-[#8d8a83] hover:text-[#f5f2eb] hover:border-white/30"
                }`}
              >
                {catLabel(c)}
              </button>
            ))}
          </div>
        </div>

        {filter === "nic" ? (
          <motion.div
            key="nic"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="flex items-center justify-center py-16 md:py-24 select-none"
            data-testid="gallery-nic"
          >
            <svg
              viewBox="0 0 120 100"
              className="w-[40vw] max-w-[320px] h-auto"
              aria-label="∞"
            >
              <path
                d="M30,50 C30,30 50,30 60,50 C70,70 90,70 90,50 C90,30 70,30 60,50 C50,70 30,70 30,50 Z"
                fill="none"
                stroke="#d97925"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </motion.div>
        ) : filtered.length === 0 ? (
          <p className="text-[#8d8a83] py-20 text-center font-light">{t("gallery.empty")}</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7">
            {filtered.map((p, i) => (
              <motion.figure
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                className="cursor-pointer group"
                onClick={() => setActive(i)}
                data-testid={`painting-${p.id}`}
              >
                <div className="relative overflow-hidden bg-[#0a0a0a] aspect-[4/3]">
                  <img
                    src={resolveImg(p.image_url)}
                    alt={lang === "sk" ? p.title_sk : p.title_en}
                    loading="lazy"
                    className="w-full h-full object-cover block spotlight"
                  />
                  <span
                    data-testid={`painting-number-${p.id}`}
                    className="absolute top-3 left-3 font-poster text-sm tracking-[0.15em] text-[#d97925] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {p.size && (
                    <span
                      data-testid={`painting-size-${p.id}`}
                      className="absolute top-3 right-3 overline text-[#f5f2eb] bg-black/40 px-2 py-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    >
                      {p.size}
                    </span>
                  )}
                </div>
              </motion.figure>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {current && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black flex items-center justify-center"
            data-testid="lightbox"
            onClick={close}
          >
            <button
              data-testid="lightbox-close"
              onClick={close}
              className="absolute top-6 right-6 text-[#a3a09a] hover:text-white z-10"
            >
              <X size={28} />
            </button>
            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-4 md:left-8 text-[#a3a09a] hover:text-white z-10"
            >
              <ChevronLeft size={40} />
            </button>
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-4 md:right-8 text-[#a3a09a] hover:text-white z-10"
            >
              <ChevronRight size={40} />
            </button>
            <motion.div
              key={current.id}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="max-w-[92vw] max-h-[88vh] flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <img
                  src={resolveImg(current.image_url)}
                  alt={lang === "sk" ? current.title_sk : current.title_en}
                  className="max-w-[92vw] max-h-[78vh] object-contain"
                />
                {current.size && (
                  <span
                    data-testid="lightbox-size"
                    className="absolute top-3 right-3 overline text-[#f5f2eb] bg-black/50 px-2.5 py-1 rounded-sm"
                  >
                    {current.size}
                  </span>
                )}
              </div>
              <div className="mt-5 text-center max-w-2xl">
                <span className="block font-poster text-base tracking-[0.2em] text-[#d97925] mb-2">
                  {String(active + 1).padStart(2, "0")}
                </span>
                <button
                  data-testid="lightbox-interest-button"
                  onClick={(e) => { e.stopPropagation(); handleInterest(String(active + 1).padStart(2, "0"), lang === "sk" ? current.title_sk : current.title_en); }}
                  className="mt-2 inline-flex items-center gap-2 border border-[#d97925] text-[#d97925] hover:bg-[#d97925] hover:text-[#050505] transition-colors px-6 py-3 rounded-full overline"
                >
                  {t("gallery.interested")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
